import * as Sentry from '@sentry/react';

/**
 * Utility class for tracking Clerk frontend API calls with Sentry diagnostics
 * Helps identify rate limiting issues and track API usage patterns
 */
export class ClerkFrontendDiagnostics {
  private static callCounts = new Map<string, number>();
  private static lastResetTime = Date.now();
  private static readonly RESET_INTERVAL = 60000; // 1 minute

  /**
   * Track a Clerk API call with Sentry breadcrumb and metrics
   */
  static trackCall(operation: string, context: Record<string, any> = {}): void {
    if (!Sentry.isInitialized()) {
      console.log(`[CLERK_FRONTEND] ${operation}`, context);
      return;
    }

    const now = Date.now();

    // Reset counters every minute
    if (now - this.lastResetTime > this.RESET_INTERVAL) {
      this.callCounts.clear();
      this.lastResetTime = now;
    }

    // Increment call counter
    const currentCount = this.callCounts.get(operation) || 0;
    this.callCounts.set(operation, currentCount + 1);

    // Add Sentry breadcrumb
    Sentry.addBreadcrumb({
      category: 'clerk-api',
      message: `Clerk Frontend API: ${operation}`,
      level: 'info',
      data: {
        operation,
        callCount: currentCount + 1,
        timestamp: new Date(now).toISOString(),
        ...context,
      },
    });

    // Log for local debugging
    console.log(`[CLERK_FRONTEND] ${operation}`, {
      callCount: currentCount + 1,
      ...context,
    });
  }

  /**
   * Track a successful Clerk API response
   */
  static trackSuccess(operation: string, context: Record<string, any> = {}): void {
    if (!Sentry.isInitialized()) return;

    Sentry.addBreadcrumb({
      category: 'clerk-api',
      message: `Clerk Frontend API Success: ${operation}`,
      level: 'info',
      data: {
        operation,
        status: 'success',
        ...context,
      },
    });
  }

  /**
   * Track a Clerk API error with enhanced logging for rate limits
   */
  static trackError(operation: string, error: unknown, context: Record<string, any> = {}): void {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    const isRateLimit = this.isRateLimitError(error);

    // Add breadcrumb for the error
    if (Sentry.isInitialized()) {
      Sentry.addBreadcrumb({
        category: 'clerk-api',
        message: `Clerk Frontend API Error: ${operation}`,
        level: 'error',
        data: {
          operation,
          error: errorMessage,
          isRateLimit,
          callCounts: Object.fromEntries(this.callCounts),
          ...context,
        },
      });

      // If it's a rate limit error, send a specific Sentry event
      if (isRateLimit) {
        Sentry.captureMessage(`Clerk Frontend Rate Limit Hit: ${operation}`, {
          level: 'error',
          tags: {
            operation,
            service: 'host-frontend',
            error_type: 'rate_limit',
          },
          contexts: {
            clerk_api: {
              operation,
              error: errorMessage,
              callCounts: Object.fromEntries(this.callCounts),
              ...context,
            },
          },
        });

        console.error(`[CLERK_FRONTEND] RATE LIMIT HIT: ${operation}`, {
          error: errorMessage,
          callCounts: Object.fromEntries(this.callCounts),
          ...context,
        });
      } else {
        // For other errors, just log them
        console.error(`[CLERK_FRONTEND] Error: ${operation}`, {
          error: errorMessage,
          ...context,
        });
      }
    } else {
      console.error(`[CLERK_FRONTEND] Error (Sentry not initialized): ${operation}`, {
        error: errorMessage,
        isRateLimit,
        ...context,
      });
    }
  }

  /**
   * Check if an error is a rate limit error (429)
   */
  private static isRateLimitError(error: unknown): boolean {
    if (!error) return false;

    // Check for 429 status code in various error formats
    const errorObj = error as any;

    // Check status code
    if (errorObj.status === 429 || errorObj.statusCode === 429) {
      return true;
    }

    // Check error message
    const message = error instanceof Error ? error.message : String(error);
    if (message.toLowerCase().includes('rate limit') ||
        message.toLowerCase().includes('429') ||
        message.toLowerCase().includes('too many requests')) {
      return true;
    }

    // Check for Clerk-specific error codes
    if (errorObj.errors && Array.isArray(errorObj.errors)) {
      return errorObj.errors.some((e: any) =>
        e.code === 'rate_limit_exceeded' ||
        e.message?.toLowerCase().includes('rate limit')
      );
    }

    return false;
  }

  /**
   * Wrap a Clerk API call with diagnostics tracking
   */
  static async wrapClerkCall<T>(
    operation: string,
    apiCall: () => Promise<T>,
    context: Record<string, any> = {}
  ): Promise<T> {
    this.trackCall(operation, context);

    try {
      const result = await apiCall();
      this.trackSuccess(operation, context);
      return result;
    } catch (error) {
      this.trackError(operation, error, context);
      throw error;
    }
  }

  /**
   * Get current call counts for monitoring
   */
  static getCallCounts(): Record<string, number> {
    return Object.fromEntries(this.callCounts);
  }
}

